/**
 * TogglePill Component
 *
 * A styled toggle switch component for active/inactive states.
 * Displays as a pill-shaped toggle with "Active" / "Inactive" labels.
 */

import React from 'react';
import { Switch, Box, Typography, styled } from '@mui/material';

interface TogglePillProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
}

const StyledBox = styled(Box)<{ checked: boolean }>(({ theme, checked }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '20px',
  backgroundColor: checked
    ? theme.palette.success.light + '20'
    : theme.palette.grey[200],
  border: `1px solid ${
    checked ? theme.palette.success.main : theme.palette.grey[400]
  }`,
  transition: 'all 0.2s ease-in-out',
}));

const TogglePill: React.FC<TogglePillProps> = ({
  checked,
  onChange,
  disabled = false,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  return (
    <StyledBox checked={checked}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: checked ? 'success.main' : 'text.secondary',
          fontSize: '0.75rem',
        }}
      >
        {checked ? activeLabel : inactiveLabel}
      </Typography>
      <Switch
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        size="small"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: 'success.main',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'success.main',
          },
        }}
      />
    </StyledBox>
  );
};

export default TogglePill;





